import {
  compositeSpecProblem,
  type BrandKit,
  type CompositeSpec,
  type LogoSlot,
  type UploadedImage,
  type UploadedVideo,
} from '@/types';

import type { BrandKitsService } from './brandKitsService';
import type { MeResponse, MeService } from './meService';
import type { UploadsService } from './uploadsService';

/**
 * Asset Library — UI 端用的高階 facade,編排 3-step 上傳。
 *
 * 後端的「intent → PUT GCS → finalize」三階段協議是 implementation detail;UI 不該感知。
 * 本介面 4 步流程:
 * 1. 前端先驗(mime / size / SVG magic byte),失敗直接拋
 * 2. 對應 service 的 `createIntent` 拿 signed URL(一般 image vs brand kit logo 端點不同)
 * 3. `gcsPut` 直 PUT 檔案到 GCS(裸 fetch,不帶 cookie / 不帶 CSRF)
 * 4. 對應 service 的 `finalize` 通知後端,拿 entity 回傳
 *
 * `gcsPut` 是注入點:正式注入真 fetch + GCS headers(見 `createHttpGcsPut`);
 * 測試可注入 stub 攔截 PUT。把 transport 抽成參數讓 facade 編排邏輯保持可測。
 *
 * Purpose-agnostic:facade 不知道 asset 給誰用,caller 自行決定持久化(graph node /
 * brand kit store / project create flow)。
 *
 * @see docs/UPLOADS.md §3 / docs/BRAND_KITS.md
 */

/**
 * GCS direct PUT 函式型別。
 *
 * 實作必須:
 * - 用裸 fetch(不走 `httpRequestWithRefresh`)
 * - 不帶 `credentials: 'include'`
 * - 帶 `Content-Type: <mime>` 跟 intent 宣告完全一致
 * - 帶 `x-goog-content-length-range: <sizeRangeHeader>`
 *
 * sizeRangeHeader 由 caller 構造,正確值是 `0,${file.size}`。GCS signed URL 把
 * `x-goog-content-length-range` 包進 `X-Goog-SignedHeaders`,PUT 時帶的 header
 * value 必須跟後端 sign 時用的完全一致,否則 GCS 回 403 `SignatureDoesNotMatch`。
 * 後端 `create_*_intent` 直接透傳 client 給的 `size_bytes` 進 sign 函式,所以是
 * `file.size`(不是 `intent.maxSizeBytes`,那是 5MB / 10MB 上限,屬於不同欄位)。
 *
 * 測試用的 stub `gcsPut` 通常不模擬 GCS signature verify,介面對齊但忽略 header
 * value;真正的 `SignatureDoesNotMatch` 驗證只在打真 GCS 時才發生。
 */
export type GcsPutFn = (
  uploadUrl: string,
  file: File,
  sizeRangeHeader: string,
) => Promise<void>;

/** 前端先擋的 image mime 白名單;不包含 SVG(防偽裝 XSS)。 */
export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;

/**
 * 一般 image 上限 30MB(R423:owner 2026-10-22「4K 也能上傳」;原 10MB)。
 *
 * 對齊 BE env `GCS_IMAGE_MAX_SIZE_BYTES`(要同批改成 31457280,否則 intent 照樣 413 ——
 * 那時畫面講的是 wire `details.max_size_bytes`,不會說謊,只是上不去)。也剛好等於 BE
 * 生成端單張原圖上限 `_image_limits.MAX_SOURCE_BYTES`(30 MiB),再往上抬就是「上傳得了、
 * 生成時才被拒」。⚠️ 說明文案的 MB 數一律由這顆常數帶(`dropzone.constraints` 的
 * `{{mb}}`),不要再寫進 i18n 字串。
 */
export const IMAGE_MAX_SIZE_BYTES = 30 * 1024 * 1024;

/**
 * 影片卡素材格的圖片上限 10MB(R423.1:owner 2026-10-22「影片卡片的欄位 10MB 維持原本,
 * 其他都能上傳 30MB」)。影片卡的圖 BE 會拿原圖直接用(omni 首幀 `_I2V_IMAGE_MAX_BYTES` /
 * reel 產品圖 `_PRODUCT_IMAGE_MAX_BYTES` 都是 10MB),放 30MB 進去 = 上傳成功、生成才失敗。
 * 哪些卡算影片卡只有 `cardImageMaxBytes`(features/canvas/cardUploadLimits)一處判斷。
 * ⚠️ 這是 FE 預檢:BE intent 收的是 `IMAGE_MAX_SIZE_BYTES`(不分卡型)。
 */
export const VIDEO_CARD_IMAGE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** Brand kit logo 上限 5MB。 */
export const LOGO_MAX_SIZE_BYTES = 5 * 1024 * 1024;

/** 前端先擋的 video mime 白名單(對齊 BE `gcs_video_allowed_mime_types` default)。 */
export const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm'] as const;

/**
 * video 上限 200MB(R423:owner 2026-10-22「15 秒 4K 要能上傳,再加一點」;原 50MB)。
 *
 * 算法:4K 60fps 手機 / 剪輯輸出的 H.264 約 100 Mbps → 15 秒 ≈ 188MB,取 200MB 留餘裕
 * (4K 30fps 約 50 Mbps → 15 秒 ≈ 94MB,綽綽有餘)。對齊 BE env `GCS_VIDEO_MAX_SIZE_BYTES`
 * (要同批改成 209715200)。秒數上限是另一顆(`GCS_VIDEO_MAX_DURATION_SECONDS`,現值 30),
 * 15 秒本來就過得了,不用動。⚠️ 說明文案的 MB 數由這顆常數帶(`{{videoMb}}`)。
 */
export const VIDEO_MAX_SIZE_BYTES = 200 * 1024 * 1024;

/**
 * 影片上傳時長上限 —— **顯示 fallback**,對齊 BE env `GCS_VIDEO_MAX_DURATION_SECONDS`
 * (FB-20260828-0029 拍板調 30;owner 2026-08-29 回話 env 已改 —— docs/107 A-1)。
 *
 * ⚠️ 只准拿來寫進說明文案(FB-20260828-0029:提示與實界兩個數字對打的事故),
 * **不可拿它在 FE 硬擋上傳**(R290 紅線:超限一律讓 BE 回 400
 * `UPLOAD_DURATION_EXCEEDS_LIMIT`)。R292.1 起秒數優先讀 **API 回的值**
 * ({@link videoUploadMaxSecondsHint}:intent 成功回應 / 400 details 都帶
 * `max_duration_seconds`,一看到就記進 session 快取),這顆常數只是還沒打過
 * API 時的初始 fallback —— 個別環境 env 尚未重部署時,第一次上傳就會被 wire
 * 值糾正回來,不會硬擋。
 */
export const VIDEO_UPLOAD_MAX_SECONDS_DISPLAY = 30;

/**
 * session 內最後一次從 wire 看到的影片時長上限(intent 成功回應或 400 details)。
 * in-memory 而已 —— 不是 browser storage,不進鑰匙總表;重整就回 fallback,
 * 下一次上傳又會學到。
 */
let lastWireVideoMaxSeconds: number | null = null;

/** 記下 wire 回報的時長上限(intent 成功 / `UPLOAD_DURATION_EXCEEDS_LIMIT` details)。 */
export function noteVideoUploadMaxSeconds(seconds: unknown): void {
  if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) {
    lastWireVideoMaxSeconds = seconds;
  }
}

/** 說明文案要顯示的影片時長上限:wire 學到的值優先,否則 fallback 常數。 */
export function videoUploadMaxSecondsHint(): number {
  return lastWireVideoMaxSeconds ?? VIDEO_UPLOAD_MAX_SECONDS_DISPLAY;
}

/** 測試用:清掉 session 快取(模組級狀態會跨測試殘留,吃到別條測試 note 的值)。 */
export function _resetVideoUploadLimitsForTest(): void {
  lastWireVideoMaxSeconds = null;
}

export interface AssetLibrary {
  /**
   * 上傳一張一般圖片(用於 canvas root node 等場景)。
   *
   * 流程:local 驗(mime / size `IMAGE_MAX_SIZE_BYTES` / magic byte)→ intent → GCS PUT → finalize。
   *
   * @param file - 來源檔案(必須通過 `validateImageFile` 三道驗證)
   * @returns 完成的 `UploadedImage` 含 `uploadId` / `gcsPath`
   * @param opts.composite - ADR-057 / docs/140 §4.2:這張是前端合成圖時帶的標記(每格 =
   *   **已上傳**的原圖 id)。送 intent 前先過 `compositeSpecProblem`,不合法直接丟
   *   `composite_invalid:{reason}`(不打後端)。一般圖片不要帶。
   * @throws `Error` 帶 message `invalid_mime` / `file_too_large` / `svg_rejected` /
   *   `composite_invalid:{reason}` / `gcs_put_failed:{status}`,或後端拋的 `ApiHttpError`
   */
  uploadImage(file: File, opts?: { composite?: CompositeSpec }): Promise<UploadedImage>;

  /**
   * 上傳一支影片(靈感來源頁 M-02 等場景;BE `/uploads/videos/*` T13 已就緒)。
   *
   * 流程:local 驗(mime mp4/webm / size `VIDEO_MAX_SIZE_BYTES` / magic byte)→ 讀影片時長(BE intent
   * 必帶 `duration_seconds`,有時長上限驗證;env `GCS_VIDEO_MAX_DURATION_SECONDS`,超限回
   * `UPLOAD_DURATION_EXCEEDS_LIMIT`)→ intent → GCS PUT → finalize。
   *
   * @param file - 來源檔案(必須通過 `validateVideoFile` 三道驗證)
   * @returns 完成的 `UploadedVideo` 含 `uploadId` / `durationSeconds`
   * @throws `Error` 帶 message `invalid_mime` / `file_too_large` / `video_magic_rejected` /
   *   `video_meta_failed`,或後端拋的 `ApiHttpError`(`UPLOAD_DURATION_EXCEEDS_LIMIT` 等)
   */
  uploadVideo(file: File): Promise<UploadedVideo>;

  /**
   * 上傳 brand kit 三 slot logo 之一。
   *
   * 流程:local 驗(mime / size 5MB / magic byte)→ brand-kit-scoped intent →
   * GCS PUT → finalize(帶 slot 防 hijack)。
   *
   * @param brandKitId - 必須是本人 active brand kit
   * @param file - 來源檔案(必須通過 `validateImageFile` 三道驗證)
   * @param slot - `primary` / `mono` / `square`;finalize 階段必須跟 intent 一致
   * @returns 完整更新後的 `BrandKit`,`logoUrls.{slot}` 含 fresh download URL
   * @throws `uploadImage` 的 client-side errors,加上後端的 `BRAND_KIT_NOT_FOUND` /
   *   `UPLOAD_INTENT_NOT_FOUND`
   */
  uploadBrandKitLogo(brandKitId: string, file: File, slot: LogoSlot): Promise<BrandKit>;

  /**
   * 上傳使用者頭像(T2b / docs/50 — M-16 個人頁)。
   *
   * 流程同 logo:local 驗(mime / size 10MB / magic byte)→ `POST /me/avatar/intent`
   * → GCS PUT → `POST /me/avatar/finalize`。
   *
   * @param file - 來源檔案(必須通過 `validateImageFile`)
   * @returns 完整 `MeResponse`,`profile.photoUrl` 是 fresh signed URL(勿長期快取)
   * @throws `uploadImage` 的 client-side errors,加上後端 `UPLOAD_*`
   */
  uploadAvatar(file: File): Promise<MeResponse>;
}

/**
 * 讀檔頭 magic byte 驗 image 真實型別（防 SVG 偽裝成 image/png 等）。
 *
 * **為什麼前端也要驗**（後端會驗 = 雙重防線）：上傳前 UI 會先用
 * ``URL.createObjectURL`` 預覽選到的檔；若不在這裡擋 SVG，預覽時 inline SVG 就可能
 * 觸發 XSS（不必等後端拒絕，傷害已在 client 端發生）。
 *
 * 只讀前 12 bytes 即可判斷：
 *   - PNG: ``89 50 4E 47 0D 0A 1A 0A``
 *   - JPEG: ``FF D8 FF`` (前 3 byte)
 *   - WebP: ``52 49 46 46 .. .. .. .. 57 45 42 50`` (RIFF...WEBP)
 *   - SVG: 文字檔開頭通常是 ``<?xml`` / ``<svg`` — 不會在以上 binary signature
 */
async function assertImageMagicByte(file: File): Promise<void> {
  const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const isPng =
    head[0] === 0x89 &&
    head[1] === 0x50 &&
    head[2] === 0x4e &&
    head[3] === 0x47 &&
    head[4] === 0x0d &&
    head[5] === 0x0a &&
    head[6] === 0x1a &&
    head[7] === 0x0a;
  const isJpeg = head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff;
  const isWebp =
    head[0] === 0x52 && // R
    head[1] === 0x49 && // I
    head[2] === 0x46 && // F
    head[3] === 0x46 && // F
    head[8] === 0x57 && // W
    head[9] === 0x45 && // E
    head[10] === 0x42 && // B
    head[11] === 0x50; // P

  if (!isPng && !isJpeg && !isWebp) {
    throw new Error('svg_rejected');
  }
}

/**
 * 共用的 image 驗證:mime 白名單 + size + magic byte 三道。
 *
 * 給 `uploadImage`(10MB)跟 `uploadBrandKitLogo`(5MB)共用。`ImageUploadDropzone`
 * 也呼叫此函式,確保 caller-injectable upload 不可 bypass mime / size / magic byte
 * 任一道驗證(security red line)。
 *
 * @param file - 來源檔案
 * @param maxBytes - 上限 byte 數(image 用 `IMAGE_MAX_SIZE_BYTES`,logo 用 5MB)
 * @throws `Error('invalid_mime')` / `Error('file_too_large')` / `Error('svg_rejected')`
 */
export async function validateImageFile(file: File, maxBytes: number): Promise<void> {
  if (
    !ALLOWED_IMAGE_MIME_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_MIME_TYPES)[number])
  ) {
    throw new Error('invalid_mime');
  }
  if (file.size > maxBytes) {
    throw new Error('file_too_large');
  }
  await assertImageMagicByte(file);
}

/**
 * 讀檔頭 magic byte 驗 video 真實型別(對齊 BE `app/uploads/magic_byte.py` 同一組
 * signature;雙重防線,前端先擋偽裝 mime):
 *   - MP4 / ISO BMFF: bytes[4:8] == "ftyp"(前 4 byte 是 box size)
 *   - WebM / Matroska: EBML signature ``1A 45 DF A3``
 */
async function assertVideoMagicByte(file: File): Promise<void> {
  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  const isMp4 =
    head.length >= 8 &&
    head[4] === 0x66 && // f
    head[5] === 0x74 && // t
    head[6] === 0x79 && // y
    head[7] === 0x70; // p
  const isWebm =
    head.length >= 4 &&
    head[0] === 0x1a &&
    head[1] === 0x45 &&
    head[2] === 0xdf &&
    head[3] === 0xa3;
  if (!isMp4 && !isWebm) {
    throw new Error('video_magic_rejected');
  }
}

/**
 * 共用的 video 驗證:mime 白名單 + size + magic byte 三道(對齊 `validateImageFile`
 * 的 security red line — dropzone / caller-injectable upload 不可 bypass)。
 *
 * @throws `Error('invalid_mime')` / `Error('file_too_large')` / `Error('video_magic_rejected')`
 */
export async function validateVideoFile(file: File, maxBytes: number): Promise<void> {
  if (
    !ALLOWED_VIDEO_MIME_TYPES.includes(file.type as (typeof ALLOWED_VIDEO_MIME_TYPES)[number])
  ) {
    throw new Error('invalid_mime');
  }
  if (file.size > maxBytes) {
    throw new Error('file_too_large');
  }
  await assertVideoMagicByte(file);
}

/**
 * 讀影片時長(秒)— BE `POST /uploads/videos/intent` 必帶 `duration_seconds`
 * (有時長上限驗證)。用 `<video preload="metadata">` + objectURL 在 client 端讀;
 * 讀不出(壞檔 / 不支援的 codec)拋 `video_meta_failed`。
 *
 * 測試注意:jsdom 不會真的 load 影片 metadata → facade 走 DI(`AssetLibraryDeps.
 * readVideoDuration`)讓測試注入 stub;本函式是 production default impl。
 */
export function readVideoDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };
    video.onloadedmetadata = () => {
      const d = video.duration;
      cleanup();
      if (!Number.isFinite(d) || d <= 0) {
        reject(new Error('video_meta_failed'));
        return;
      }
      resolve(d);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('video_meta_failed'));
    };
    video.src = url;
  });
}

interface AssetLibraryDeps {
  uploads: UploadsService;
  brandKits: BrandKitsService;
  me: MeService;
  gcsPut: GcsPutFn;
  /**
   * 讀影片時長(秒)— 預設 `readVideoDurationSeconds`(objectURL + video element)。
   * 注入點讓測試 stub(jsdom 不會真 load metadata),對齊 `gcsPut` 的 DI 慣例。
   */
  readVideoDuration?: (file: File) => Promise<number>;
}

export function createAssetLibrary(deps: AssetLibraryDeps): AssetLibrary {
  const { uploads, brandKits, me, gcsPut } = deps;
  const readVideoDuration = deps.readVideoDuration ?? readVideoDurationSeconds;

  return {
    async uploadImage(file, opts) {
      await validateImageFile(file, IMAGE_MAX_SIZE_BYTES);
      // ADR-057 — 合成圖標記先在本地驗(BE 422 `UPLOAD_COMPOSITE_INVALID` 為權威;
      // 先擋省一次 intent + 一張檔案的 PUT)。
      const composite = opts?.composite;
      if (composite) {
        const problem = compositeSpecProblem(composite);
        if (problem) throw new Error(`composite_invalid:${problem}`);
      }

      const intent = await uploads.images.createIntent({
        mimeType: file.type,
        sizeBytes: file.size,
        ...(composite ? { composite } : {}),
      });

      // GCS direct PUT — 用獨立 gcsPut,不繼承 wrapper 的 cookie / CSRF / refresh。
      // sizeRangeHeader 必須是 `0,${file.size}`(不是 `intent.maxSizeBytes`):backend 在
      // sign URL 時把 client `size_bytes` 透傳進 SignedHeaders 計算 signature,PUT 帶
      // 完全相同的 file.size 才能通過 GCS signature verify。
      await gcsPut(intent.uploadUrl, file, `0,${file.size}`);

      return uploads.images.finalize(intent.uploadId);
    },

    async uploadVideo(file) {
      await validateVideoFile(file, VIDEO_MAX_SIZE_BYTES);

      // BE intent 必帶 duration_seconds(時長上限驗證,default 10s)— client 端讀 metadata。
      const durationSeconds = await readVideoDuration(file);

      const intent = await uploads.videos.createIntent({
        mimeType: file.type,
        sizeBytes: file.size,
        durationSeconds,
      });
      // docs/107 A-1 — intent 回的時長上限記進快取,說明文案跟著 wire 走不再漂移
      noteVideoUploadMaxSeconds(intent.maxDurationSeconds);

      // sizeRangeHeader 同 image:兩處 file.size 必相同(intent body / PUT header)。
      await gcsPut(intent.uploadUrl, file, `0,${file.size}`);

      return uploads.videos.finalize(intent.uploadId);
    },

    async uploadBrandKitLogo(brandKitId, file, slot) {
      await validateImageFile(file, LOGO_MAX_SIZE_BYTES);

      const intent = await brandKits.createLogoIntent(brandKitId, {
        mimeType: file.type,
        sizeBytes: file.size,
        slot,
      });

      // sizeRangeHeader 對齊 backend `create_logo_intent` 透傳 client `size_bytes` 給
      // GCS sign 的設計;兩處 file.size 必相同(intent body / PUT header)。
      await gcsPut(intent.uploadUrl, file, `0,${file.size}`);

      // 帶 intent.slot(server 端記住的 slot)給 finalize 對齊 intent;不一致後端會回
      // UPLOAD_INTENT_NOT_FOUND(防 hijack)。
      return brandKits.finalizeLogo(brandKitId, intent.uploadId, intent.slot);
    },

    async uploadAvatar(file) {
      // 頭像走 BE 一般 image 上限(gcs_image_max_size_bytes);沿用同一顆 pre-check。
      await validateImageFile(file, IMAGE_MAX_SIZE_BYTES);

      const intent = await me.createAvatarIntent({
        mimeType: file.type,
        sizeBytes: file.size,
      });

      // sizeRangeHeader 對齊 backend 透傳 client size_bytes 給 GCS sign 的設計。
      await gcsPut(intent.uploadUrl, file, `0,${file.size}`);

      return me.finalizeAvatar(intent.uploadId);
    },
  };
}
